import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Input, AutoComplete } from 'antd/es';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getAllViewElementsRedux } from 'src/redux/actions/viewActionsRedux';
import { connect } from 'react-redux';
import store from 'src/redux/store/reduxStoreIndex';
import { CustomIcon } from 'src/utils/icon-list';
import { HashLink } from 'react-router-hash-link';
import he from 'he';

const useStyles = createUseStyles({
	categorySearch: {
		width: 500,
	},
	categorySearchDropdown: {
		// otherwise the search results would scroll with the main page
		position: 'fixed',
	},
});

// returns an array of all headings
const unwindWikiTree = (tree) => {
	const resultArray = [];
	tree.forEach((node, i) => {
		resultArray.push({
			hashLink: node.title,
			key: node.key,
			name: node.searchQuery ? node.searchQuery : node.title,
		});
		if (node?.children?.length > 0) {
			resultArray.push(...unwindWikiTree(node.children));
		}
	});

	return resultArray;
};

const Search = (props) => {
	const { activeModel, views } = props;
	const { model } = activeModel;
	const classes = useStyles();
	const { t, i18n } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const navigate = useNavigate();

	const searchFunc = async (query) => {
		const encodedQuery = encodeURIComponent(query.toLowerCase());

		const allViews = await getAllViews();

		if (encodedQuery !== '') {
			const res = [];
			for (const viewsOfTitle of allViews) {
				res.push({
					title: viewsOfTitle.title,
					children: viewsOfTitle.children.filter(
						(view) =>
							encodeURIComponent(view.name.toLowerCase()).includes(encodedQuery) ||
							(view.searchQuery && encodeURIComponent(view.searchQuery.toLowerCase()).includes(encodedQuery))
					),
				});
			}
			setSearchResults(res);
		} else {
			setSearchResults(allViews);
		}
	};

	const handleSearch = (value) => {
		let searchValue = value;
		if (typeof value === 'string') {
			setSearchQuery(value);
			// this case is the onClick case
		} else if (value?.nativeEvent?.target?.value) {
			searchValue = value?.nativeEvent?.target?.value;
			setSearchQuery(searchValue);
		} else {
			searchValue = searchQuery;
		}
		searchFunc(searchValue);
	};

	const renderTitle = (title, count) => (
		// render title of category and number of results in antd autocomplete
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
			}}
		>
			<span>{title}</span>
			<span>
				{count ? `${t('search.RESULTSNUMBER')}:` : null} {count}
			</span>
		</div>
	);

	const renderItem = (data) =>
		// render items in antd autocomplete
		({
			value: data.name,
			key: data.key,
			link: `/model/${model.slug}/view/${data.parentID}/?guid=${data.guid}`,
			label: (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
					}}
				>
					{data.hashLink ? (
						data.hashLink
					) : (
						<Link to={`/model/${model.slug}/view/${data.parentID}/?guid=${data.guid}`}>
							{he.decode(String(data.sign))}-{he.decode(String(data.name))}
						</Link>
					)}
				</div>
			),
		});

	const formatQueryResult = (data) => {
		if (data?.length === 0 && searchQuery !== '') {
			// when there are no results, display this
			return [
				{
					label: renderTitle(t('search.NORESULTS')),
					options: [],
				},
			];
		}

		return data.map((group) => ({
			label: renderTitle(group.title, group.children.length),
			options: group.children.map((child) => renderItem(child)),
		}));
	};

	const getAllViews = async () => {
		const formatViewObject = (view, parentID) => ({
			name: view.name,
			guid: view.guid,
			key: `${view.name}-${view.guid}-${view.key}-${view._id}`,
			parentID,
			sign: view.key || '',
		});

		const allViews = [];
		if (views.isFetching || views.error) {
			return allViews;
		}
		for (const view of views.items) {
			const parentID = view._id;

			// make static headings searchable
			if (view.viewType === 'static') {
				if (view?.activeLayout?.resultTree?.length > 0) {
					const allHeadings = unwindWikiTree(view.activeLayout.resultTree);
					allHeadings.forEach((header, i) => {
						const modifiedHeader = { ...header, key: `${parentID}-${header.key}` };
						modifiedHeader.hashLink = !React.isValidElement(header.hashLink) ? (
							<HashLink
								id={`linkTo${modifiedHeader.key}`}
								to={`/model/${model.slug}/view/${parentID}#${modifiedHeader.key}`}
								scroll={(el) => {
									const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
									const yOffset = -70;
									window.scrollTo({
										top: yCoordinate + yOffset,
										behavior: 'smooth',
									});
								}}
								className={classes.subMenu}
							>
								{' '}
								{modifiedHeader.name}{' '}
							</HashLink>
						) : (
							{ ...modifiedHeader.hashLink, props: { ...modifiedHeader.hashLink.props, className: '' } }
						);
						allHeadings[i] = modifiedHeader;
					});

					allViews.push({
						title: view.displayTitle[i18n.language],
						children: allHeadings,
					});
				}
			} else {
				// get all elements of this view, format them and put them in one array
				const allViewElements = [];

				if (view.allElements) {
					view.allElements.forEach((el) => allViewElements.push(formatViewObject(el, parentID)));
				} else {
					const tmp = await store.dispatch(getAllViewElementsRedux(model._id, view._id));
					if (tmp?.allElements) {
						tmp.allElements.forEach((el) => allViewElements.push(formatViewObject(el, parentID)));
					}
				}

				// afterwards we sort them by key or by name
				allViewElements.sort((a, b) => {
					let categoryA;
					let categoryB;
					// if we have a number we use it to sort (usually process view), if not we use the name (usually object view)
					if (a.sign && b.sign) {
						categoryA = parseInt(a.sign, 10);
						categoryB = parseInt(b.sign, 10);
					} else {
						categoryA = a.name.toUpperCase();
						categoryB = b.name.toUpperCase();
					}

					if (categoryA < categoryB) {
						return -1;
					}
					if (categoryA > categoryB) {
						return 1;
					}
					// both are the same
					return 0;
				});

				allViews.push({ title: view.displayTitle[i18n.language], children: allViewElements });
			}
		}
		return allViews;
	};

	const handleSelection = (value, option) => {
		searchFunc(value);
		navigate(option.link);
	};

	// const formatViewObject = (view, parentID) => ({
	// 	name: view.name,
	// 	guid: view.guid,
	// 	key: `${view.name}-${view.guid}-${view.key}-${view._id}`,
	// 	parentID,
	// 	sign: view.key || '',
	// });

	return (
		<AutoComplete
			className={classes.categorySearch}
			popupClassName={classes.categorySearchDropdown}
			popupMatchSelectWidth
			options={formatQueryResult(searchResults) || []}
			value={searchQuery}
			onSearch={handleSearch}
			onClick={handleSearch}
			onSelect={handleSelection}
		>
			<Input.Search prefix={<CustomIcon title='search' />} placeholder={t('search.STANDARDTITLE')} allowClear />
		</AutoComplete>
	);
};

const mapStateToProps = (state) => ({
	activeModel: state.activeModel,
	views: state.views,
});

export default connect(mapStateToProps)(Search);
